import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import {
  allPages,
  defaultPermissions,
  roleTabs,
  roleDescriptions,
  permissionOptions,
  permissionBadge,
} from "@/mocks/rbac";
import type { PermissionItem } from "@/mocks/rbac";

interface GroupedPermissions {
  [moduleGroup: string]: PermissionItem[];
}

function groupByModule(permissions: PermissionItem[]): GroupedPermissions {
  const groups: GroupedPermissions = {};
  for (const item of permissions) {
    if (!groups[item.module_group]) groups[item.module_group] = [];
    groups[item.module_group].push(item);
  }
  return groups;
}

export default function RBACPage() {
  const [activeRole, setActiveRole] = useState("admin");
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchPermissions = useCallback(async (role: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("role_permissions")
        .select("*")
        .eq("role_level", role)
        .order("module_group", { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        const dbMap = new Map<string, PermissionItem>(data.map((d: PermissionItem) => [d.page_key, d]));
        const defaults = defaultPermissions[role] || [];

        // Merge DB records with allPages defaults (fill missing pages)
        const merged: PermissionItem[] = allPages.map((page) => {
          const dbRecord = dbMap.get(page.page_key);
          if (dbRecord) {
            return {
              page_key: dbRecord.page_key,
              page_name: dbRecord.page_name,
              module_group: dbRecord.module_group,
              permission: dbRecord.permission,
            };
          }
          const defaultItem = defaults.find((d) => d.page_key === page.page_key);
          return {
            page_key: page.page_key,
            page_name: page.page_name,
            module_group: page.module_group,
            permission: defaultItem?.permission || "hidden",
          };
        });
        setPermissions(merged);
      } else {
        setPermissions(defaultPermissions[role] || []);
      }
      setHasChanges(false);
    } catch (err) {
      console.error(err);
      setPermissions(defaultPermissions[role] || []);
      setHasChanges(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions(activeRole);
  }, [activeRole, fetchPermissions]);

  const handlePermissionChange = (pageKey: string, newPermission: string) => {
    setPermissions((prev) =>
      prev.map((p) =>
        p.page_key === pageKey
          ? { ...p, permission: newPermission as PermissionItem["permission"] }
          : p,
      ),
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        showToast("尚未登入，請重新登入", "error");
        setSaving(false);
        return;
      }

      const res = await fetch(
        `${import.meta.env.VITE_API_URL || ""}/api/functions/save-role-permissions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            role: activeRole,
            permissions: permissions.map((p) => ({
              page_key: p.page_key,
              page_name: p.page_name,
              module_group: p.module_group,
              permission: p.permission,
            })),
          }),
        },
      );

      const result = await res.json().catch(() => ({
        error: "伺服器回應異常",
      }));

      if (!res.ok || result.error) {
        throw new Error(result.error || `儲存失敗 (${res.status})`);
      }

      showToast("權限設定已儲存", "success");
      setHasChanges(false);
    } catch (err) {
      console.error("Save error:", err);
      showToast(
        err instanceof Error ? err.message : "儲存失敗，請重試",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPermissions(defaultPermissions[activeRole] || []);
    setHasChanges(true);
    showToast("已還原為預設值，請點擊儲存變更以生效", "success");
  };

  const grouped = groupByModule(permissions);
  const moduleOrder = ["首頁", "工作中心", "營運管理", "系統管理", "個人中心"];

  return (
    <div className="max-w-[1100px] mx-auto space-y-5">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-20 right-6 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium transition-all ${
            toast.type === "success"
              ? "bg-primary-500 text-background-50"
              : "bg-accent-500 text-background-50"
          }`}
        >
          <i
            className={
              toast.type === "success"
                ? "ri-check-line"
                : "ri-error-warning-line"
            }
          />
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div>
        <h1
          className="text-xl font-bold text-foreground-900"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          權限設定
        </h1>
        <p className="text-xs text-foreground-500 mt-1">
          管理超級管理員／一般職員／試用期／停職 四組權限模板・DB 為唯一同步來源
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-primary-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-foreground-900 mb-1">
              關於權限等級設定
            </h3>
            <p className="text-xs text-foreground-500 leading-relaxed">
              這裡的設定為「權限模板的初始值」，以資料庫為唯一真實來源。修改後會先寫入後端，成功後 UI 才會變色；若同步失敗，UI 會回滾並顯示錯誤。
              模組順序與名稱完全對應左側選單，新增或修改選單時自動同步。
            </p>
          </div>
          <span className="text-[10px] text-foreground-400 bg-background-100 px-2 py-1 rounded shrink-0">
            DB 為單一真實來源
          </span>
        </div>
      </div>

      {/* Role Tabs */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-1.5">
        <div className="flex gap-1">
          {roleTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveRole(tab.value)}
              className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeRole === tab.value
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Role Permission Panel */}
      <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
        {/* Role Header */}
        <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-foreground-900">
              {roleTabs.find((t) => t.value === activeRole)?.label}
            </h2>
            <p className="text-xs text-foreground-500 mt-0.5">
              {roleDescriptions[activeRole]}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="px-3 py-2 rounded-lg border border-background-300 text-xs text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              還原為預設值
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                hasChanges
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-background-100 text-foreground-400 cursor-not-allowed"
              }`}
            >
              {saving ? (
                <span className="flex items-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin" />
                  儲存中...
                </span>
              ) : (
                "儲存變更"
              )}
            </button>
          </div>
        </div>

        {/* Permission Table */}
        {loading ? (
          <div className="p-12 flex flex-col items-center gap-3">
            <i className="ri-loader-4-line animate-spin text-primary-500 text-2xl" />
            <p className="text-sm text-foreground-500">載入中...</p>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_140px_180px] px-5 py-2.5 bg-background-50">
              <span className="text-xs font-medium text-foreground-500">
                模組
              </span>
              <span className="text-xs font-medium text-foreground-500 text-center">
                目標權限（DB）
              </span>
              <span className="text-xs font-medium text-foreground-500 text-right">
                狀態
              </span>
            </div>

            {moduleOrder.map(
              (group) =>
                grouped[group] && (
                  <div key={group}>
                    {/* Module Group Header */}
                    <div className="px-5 py-2 bg-background-100/60">
                      <span className="text-xs font-semibold text-foreground-700">
                        {group}
                      </span>
                    </div>
                    {/* Module Items */}
                    {grouped[group].map((item) => {
                      const badge = permissionBadge[item.permission];
                      return (
                        <div
                          key={item.page_key}
                          className="grid grid-cols-[1fr_140px_180px] px-5 py-3 items-center hover:bg-background-50/60 transition-colors"
                        >
                          <div>
                            <span className="text-sm text-foreground-700">
                              {item.page_name}
                            </span>
                            {item.permission === "accessible" && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-50 text-primary-600">
                                可操作
                              </span>
                            )}
                          </div>
                          <div className="flex justify-center">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${badge.bg} ${badge.text}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${badge.dot}`}
                              />
                              {badge.label}
                            </span>
                          </div>
                          <div className="flex justify-end">
                            <div className="relative">
                              <select
                                value={item.permission}
                                onChange={(e) =>
                                  handlePermissionChange(
                                    item.page_key,
                                    e.target.value,
                                  )
                                }
                                className="appearance-none cursor-pointer pl-3 pr-8 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-700 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                              >
                                {permissionOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                <i className="ri-arrow-down-s-line text-foreground-400 text-xs" />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ),
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-foreground-500">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-primary-500" />
          可操作
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-secondary-500" />
          受限
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-accent-500" />
          隱藏
        </span>
      </div>
    </div>
  );
}